import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api, API, errDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Sidebar } from "../components/Sidebar";
import { MessageBlock } from "../components/MessageBlock";
import { ChatInput } from "../components/ChatInput";

const DEFAULT_PROVIDERS = {
  anthropic: ["claude-sonnet-4-6", "claude-opus-4-8", "claude-haiku-4-5-20251001"],
  openai: ["gpt-5.4", "gpt-5.5", "gpt-5.4-mini", "gpt-4o"],
  gemini: ["gemini-3.1-pro-preview", "gemini-3.5-flash", "gemini-2.5-pro"],
};

export default function ChatPage() {
  const location = useLocation();
  const { user } = useAuth();
  const [providers, setProviders] = useState(DEFAULT_PROVIDERS);
  const [selection, setSelection] = useState({ provider: "anthropic", model: "claude-sonnet-4-6" });
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [streamText, setStreamText] = useState(null);
  const [busy, setBusy] = useState(false);
  const [usage, setUsage] = useState(null);
  const [failed, setFailed] = useState(null);
  const bottomRef = useRef(null);
  const scrollRef = useRef(null);
  const atBottomRef = useRef(true);
  const abortRef = useRef(null);

  useEffect(() => {
    api.get("/models").then((r) => setProviders(r.data)).catch(() => {});
    loadConversations();
    loadUsage();
    if (location.state?.openConversation) selectConversation(location.state.openConversation);
    // Support ?open=<conv_id> from the Import bookmarklet redirect.
    const params = new URLSearchParams(location.search);
    const openId = params.get("open");
    if (openId) {
      selectConversation(openId);
      // clean the URL so refresh doesn't re-trigger
      window.history.replaceState({}, "", location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUsage = () => api.get("/usage/summary").then((r) => setUsage(r.data)).catch(() => {});

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };

  useEffect(() => {
    if (atBottomRef.current) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamText]);

  const loadConversations = async () => {
    try {
      const { data } = await api.get("/conversations");
      setConversations(data);
    } catch {}
  };

  const selectConversation = async (id) => {
    setActiveId(id);
    try {
      const { data } = await api.get(`/conversations/${id}/messages`);
      setMessages(data);
    } catch (e) {
      toast.error(errDetail(e));
    }
  };

  const newChat = () => {
    setActiveId(null);
    setMessages([]);
  };

  const deleteConversation = async (id) => {
    await api.delete(`/conversations/${id}`);
    if (id === activeId) newChat();
    loadConversations();
  };

  const toggleSave = async (c) => {
    await api.patch(`/conversations/${c.id}`, { saved: !c.saved });
    toast.success(c.saved ? "Unpinned" : "Pinned to top");
    loadConversations();
  };

  const ensureConversation = async (firstText) => {
    if (activeId) return activeId;
    const { data } = await api.post("/conversations", { title: firstText.slice(0, 60) || "New Chat" });
    setActiveId(data.id);
    return data.id;
  };

  const send = useCallback(async (text, attachList, override) => {
    setBusy(true);
    setFailed(null);
    const { provider, model } = override || selection;
    const attachmentIds = attachList.map((a) => a.id);
    let full = "";
    let convId;
    try {
      convId = await ensureConversation(text);
      setMessages((prev) => [...prev, { id: `tmp-${Date.now()}`, role: "user", content: text, attachments: attachList }]);

      if (provider === "image") {
        setStreamText({ provider: "image", model, content: "Creating your image…" });
        const { data } = await api.post("/generate-image", { prompt: text, engine: model, conversation_id: convId, attachment_ids: attachmentIds }, { timeout: 180000 });
        setStreamText(null);
        setMessages((prev) => [...prev, { id: data.asset_id, role: "assistant", content: data.text || "", provider: "image", model, asset_id: data.asset_id }]);
      } else {
        setStreamText({ provider, model, content: "" });
        const controller = new AbortController();
        abortRef.current = controller;
        const res = await fetch(`${API}/conversations/${convId}/chat`, {
          method: "POST",
          credentials: "include",
          signal: controller.signal,
          headers: { "Content-Type": "application/json", ...(localStorage.getItem("triad-token") ? { Authorization: `Bearer ${localStorage.getItem("triad-token")}` } : {}) },
          body: JSON.stringify({ content: text, provider, model, attachment_ids: attachmentIds }),
        });
        if (!res.ok) {
          let detail;
          try { detail = (await res.json()).detail; } catch {}
          throw new Error(detail || `Request failed (${res.status})`);
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n\n");
          buffer = lines.pop();
          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            const ev = JSON.parse(line.slice(6));
            if (ev.type === "delta") {
              full += ev.content;
              setStreamText({ provider, model, content: full });
            } else if (ev.type === "error") {
              throw new Error(ev.content);
            }
          }
        }
        setStreamText(null);
        setMessages((prev) => [...prev, { id: `a-${Date.now()}`, role: "assistant", content: full, provider, model }]);
      }
      loadConversations();
    } catch (e) {
      setStreamText(null);
      if (e.name === "AbortError") {
        if (full && convId) {
          setMessages((prev) => [...prev, { id: `a-${Date.now()}`, role: "assistant", content: full, provider, model, stopped: true }]);
          try { await api.post(`/conversations/${convId}/messages`, { content: full, provider, model }); } catch {}
        }
        loadConversations();
      } else {
        toast.error(errDetail(e) || e.message);
        setFailed({ text, attachList, provider, model, error: errDetail(e) || e.message });
      }
    } finally {
      abortRef.current = null;
      setBusy(false);
      loadUsage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, selection]);

  const stop = () => abortRef.current?.abort();

  const retryFromMessage = useCallback((m) => {
    const idx = messages.findIndex((x) => x.id === m.id);
    for (let i = idx - 1; i >= 0; i--) {
      if (messages[i].role === "user") {
        const attachList = (messages[i].attachments || []).filter((a) => typeof a === "object" && a.id);
        send(messages[i].content, attachList, { provider: m.provider === "image" ? "image" : m.provider, model: m.model });
        return;
      }
    }
    toast.error("Could not find the original prompt to retry");
  }, [messages, send]);


  return (
    <div className="flex h-screen bg-[var(--bg)] overflow-hidden">
      <Sidebar conversations={conversations} activeId={activeId} onSelect={selectConversation}
        onNew={newChat} onDelete={deleteConversation} onToggleSave={toggleSave} />

      <main className="flex-1 flex flex-col min-w-0">
        <div className="flex-1 overflow-y-auto px-6 py-8" data-testid="chat-thread" ref={scrollRef} onScroll={onScroll}>
          <div className="max-w-3xl mx-auto space-y-8">
            {messages.length === 0 && !streamText && (
              <div className="pt-28 text-center fade-up">
                <img src="/exotic-india-wordmark.webp" alt="Exotic India" className="h-12 w-auto mb-6 object-contain mx-auto" data-testid="chat-welcome-logo" />
                <h1 className="font-serif-display font-semibold text-4xl sm:text-5xl tracking-tight">
                  {user?.name ? `Hello, ${user.name.split(" ")[0]}` : "Hello there"}
                </h1>
                <p className="text-[15px] text-[var(--text-2)] mt-4 max-w-md mx-auto leading-relaxed">
                  Ask Claude, ChatGPT or Gemini — pick a model below, attach files, or generate images. Everything lives in one place.
                </p>
              </div>
            )}
            {(() => {
              // Detect if the *previous* user message asked for a doc / deck / pdf so we can
              // render a compact "Ready — Download" card instead of the raw markdown.
              const PPTX = /\b(pptx?|powerpoint|slide\s*deck|slides|deck|presentation)\b/i;
              const DOCX = /\b(docx?|word\s*doc(ument)?|write\s*up)\b/i;
              const PDF = /\b(pdf)\b/i;
              const intentFor = (i) => {
                if (messages[i]?.role !== "assistant") return null;
                for (let j = i - 1; j >= 0; j--) {
                  if (messages[j].role === "user") {
                    const t = (messages[j].content || "");
                    if (PPTX.test(t)) return "pptx";
                    if (DOCX.test(t)) return "docx";
                    if (PDF.test(t)) return "pdf";
                    return null;
                  }
                }
                return null;
              };
              return messages.map((m, i) => (
                <MessageBlock key={m.id} msg={m}
                  exportIntent={intentFor(i)}
                  onRetry={m.role === "assistant" ? retryFromMessage : undefined} />
              ));
            })()}
            {streamText && <MessageBlock msg={streamText} streaming />}
            {failed && !busy && (
              <div data-testid="failed-retry-row" className="flex items-center gap-3 bg-[var(--error)]/8 border border-[var(--error)]/25 rounded-2xl px-4 py-3 fade-up">
                <p className="flex-1 text-[13px] text-[var(--error)]">{failed.error || "Generation failed."}</p>
                <button data-testid="retry-failed-button"
                  onClick={() => send(failed.text, failed.attachList, { provider: failed.provider, model: failed.model })}
                  className="shrink-0 rounded-full text-white text-xs font-semibold px-4 py-2 transition-colors duration-300" style={{ background: "var(--accent)" }}>
                  Retry
                </button>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
        </div>

        {usage && usage.percent >= 80 && (
          <div data-testid="usage-banner"
            className={`mx-6 mt-3 rounded-2xl px-4 py-2.5 text-[13px] font-medium flex items-center justify-center gap-3 ${usage.percent >= 100 ? "bg-[var(--error)]/10 border border-[var(--error)]/30 text-[var(--error)]" : "bg-amber-500/10 border border-amber-500/30 text-amber-600"}`}>
            <span className="text-center">
              {usage.percent >= 100
                ? `Team monthly cap reached ($${usage.month_total.toFixed(2)} of $${usage.cap.toFixed(0)}). Chat & image generation are paused.`
                : `Heads up: ${usage.percent}% of the team's monthly budget ($${usage.month_total.toFixed(2)} of $${usage.cap.toFixed(0)}) has been used.`}
            </span>
            {user?.role === "admin" && (
              <a href="/admin" data-testid="raise-cap-link"
                className="shrink-0 rounded-full text-white text-[11px] font-semibold px-3 py-1.5 transition-colors duration-300"
                style={{ background: "var(--accent)" }}>
                {usage.percent >= 100 ? "Raise cap" : "Manage cap"}
              </a>
            )}
          </div>
        )}
        <ChatInput onSend={send} onStop={stop} busy={busy} providers={providers} selection={selection} onSelectionChange={setSelection} />
      </main>
    </div>
  );
}
